<template>
  <!--
    首页 UI 改版（运营原型 HSK-Mock-Prototype-23092026，home 屏）。
    只换 UI 与交互，业务逻辑沿用原有实现：埋点 / 路由 / 接口 / 登录门控 / 移动端下载弹窗都还是原来的方法。
    区块 class 与原型保持一致，样式见 ./home/home-v2.scss（由原型 CSS 抽取）。
    .home-v2 = 样式作用域根（原型里 JS 启用后加在 <html> 上的 .js-hsk-motion 也放这里），
    .home-v2-view = 原型的 .view[data-view="home"] 容器，抽取出来的选择器里前缀都映射到它。
    全站 Header / Footer 由 Layout 渲染，不在本组件内。
  -->
  <div class="home home-v2 js-hsk-motion">
    <div class="home-v2-view" ref="homeRoot">
      <!-- Hero -->
      <section class="home-hero">
        <div class="container">
          <div class="hero-grid">
            <div class="hero-copy">
              <div class="hero-kicker-row">
                <img
                  alt="HSK"
                  class="hero-hsk-logo"
                  :src="require('@/assets/images/home/v2/hero-hsk-mark.png')"
                />
                <span aria-hidden="true" class="hero-kicker-separator"></span>
                <div class="hero-kicker">{{ $t('home_v2_hero_kicker') }}</div>
              </div>
              <h1>
                {{ $t('home_v2_hero_title_1') }}<br />
                <span class="hero-title-line">{{ $t('home_v2_hero_title_2') }}</span>
              </h1>
              <p>{{ $t('home_v2_hero_desc') }}</p>
              <div class="hero-action">
                <!-- 业务不变：仍走 onClickHeroStart（埋点 + goCheckLevel 的智能跳转） -->
                <button class="btn btn-primary" type="button" @click="onClickHeroStart">
                  {{ $t('home_v2_choose_your_exam') }}
                </button>
              </div>
            </div>
            <div class="hero-product-wrap" :aria-label="$t('home_v2_hero_image_alt')">
              <div class="hero-product-frame">
                <img
                  class="hero-product-screen"
                  :alt="$t('home_v2_hero_image_alt')"
                  :src="require('@/assets/images/home/v2/hero-platform.png')"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Proof rail -->
      <section class="proof-rail-section" aria-label="HSK Mock platform credibility">
        <div class="container">
          <div class="proof-rail" aria-label="Why learners choose HSK Mock">
            <div class="proof-rail-item">
              <div
                aria-hidden="true"
                class="proof-rail-visual proof-rail-logo-stack proof-rail-cti-logo"
              >
                <img alt="" :src="require('@/assets/images/home/v2/proof-cti-logo.png')" />
              </div>
              <div class="proof-rail-copy">
                <strong>{{ $t('home_v2_proof_cti_title') }}</strong>
                <span>{{ $t('home_v2_proof_cti_desc') }}</span>
              </div>
            </div>
            <div class="proof-rail-item">
              <div aria-hidden="true" class="proof-rail-visual proof-rail-stat">
                <span class="proof-stat-value">600K</span>
                <span class="proof-stat-plus">+</span>
              </div>
              <div class="proof-rail-copy">
                <strong>{{ $t('home_v2_proof_learners_title') }}</strong>
                <span>{{ $t('home_v2_proof_learners_desc') }}</span>
              </div>
            </div>
            <div class="proof-rail-item">
              <div aria-hidden="true" class="proof-rail-visual proof-rail-hsk-logo">
                <img alt="" :src="require('@/assets/images/home/v2/proof-hsk-logo.png')" />
              </div>
              <div class="proof-rail-copy">
                <strong>{{ $t('home_v2_proof_authentic_title') }}</strong>
                <span>{{ $t('home_v2_proof_authentic_desc') }}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Choose your exam：五个入口都去等级选择页，tab 通过 query 带过去 -->
      <section
        class="home-section is-white exam-choice-section exam-choice-brand-section"
        id="examOptions"
      >
        <div class="container">
          <div class="home-heading">
            <h2>{{ $t('home_v2_choose_your_exam') }}</h2>
          </div>
          <div class="exam-choice-grid exam-paper-grid">
            <button
              v-for="exam in examOptions"
              :key="exam.tab"
              class="exam-choice-card exam-paper-choice"
              type="button"
              @click="goChooseExam(exam.tab)"
            >
              <span class="exam-choice-title">{{ $t(exam.labelKey) }}</span>
            </button>
          </div>
        </div>
      </section>

      <!-- AI Score Report -->
      <section class="home-section is-white ai-score-section" id="aiScoreReport" ref="aiSection">
        <div class="container">
          <div class="home-heading ai-score-heading">
            <span class="ai-score-badge">{{ $t('home_v2_ai_badge') }}</span>
            <h2>{{ $t('home_v2_ai_title') }}</h2>
            <p>{{ $t('home_v2_ai_desc') }}</p>
          </div>
          <div
            class="ai-score-carousel"
            ref="aiCarousel"
            @mouseenter="stopAiAuto"
            @mouseleave="startAiAuto"
            @focusin="stopAiAuto"
            @focusout="onAiFocusOut"
          >
            <div class="ai-score-viewport">
              <div
                class="ai-score-track"
                :style="{ transform: 'translate3d(-' + aiIndex * 100 + '%,0,0)' }"
              >
                <!-- 幻灯 1：分数总览（插图里的文字是示意数据，随原型保持英文） -->
                <article class="ai-score-slide" :class="{ 'is-active': aiIndex === 0 }">
                  <div aria-hidden="true" class="ai-score-illustration">
                    <svg role="img" viewBox="21 20 278 124">
                      <g class="ai-score-visual-card" transform="translate(24 23)">
                        <rect
                          fill="#fff"
                          height="120"
                          rx="14"
                          stroke="#ebecef"
                          width="272"
                          x="0"
                          y="0"
                        ></rect>
                        <circle
                          cx="66"
                          cy="60"
                          fill="none"
                          r="34"
                          stroke="#daf4e2"
                          stroke-width="10"
                        ></circle>
                        <circle
                          class="ai-score-ring-progress"
                          cx="66"
                          cy="60"
                          fill="none"
                          r="24.5"
                          stroke="#8ccfb8"
                          stroke-linecap="round"
                          stroke-width="10"
                          transform="rotate(-90 66 60)"
                        ></circle>
                        <text
                          class="ai-score-total"
                          ref="aiScoreValue"
                          dominant-baseline="middle"
                          fill="#2c2f46"
                          font-family="Figtree,Inter,Arial,sans-serif"
                          font-size="20"
                          font-weight="850"
                          letter-spacing="-.55"
                          style="font-variant-numeric: tabular-nums"
                          text-anchor="middle"
                          x="66"
                          y="57.5"
                        >
                          {{ aiScoreDisplay }}
                        </text>
                        <text
                          fill="#8a8e99"
                          font-family="Figtree,Inter,Arial,sans-serif"
                          font-size="8"
                          font-weight="700"
                          text-anchor="middle"
                          x="66"
                          y="74"
                        >
                          / 300
                        </text>
                        <g class="ai-score-passed" :class="{ 'is-visible': aiPassedVisible }">
                          <rect fill="#daf4e2" height="13" rx="6.5" width="42" x="45" y="87"></rect>
                          <text
                            fill="#67b99a"
                            font-family="Figtree,Inter,Arial,sans-serif"
                            font-size="5.8"
                            font-weight="900"
                            text-anchor="middle"
                            x="66"
                            y="95.8"
                          >
                            PASSED
                          </text>
                        </g>
                        <text
                          fill="#2c2f46"
                          font-family="Figtree,Inter,Arial,sans-serif"
                          font-size="8.5"
                          font-weight="800"
                          x="124"
                          y="31"
                        >
                          Listening
                        </text>
                        <rect fill="#eceef3" height="7" rx="3.5" width="116" x="124" y="37"></rect>
                        <rect
                          class="ai-score-bar"
                          fill="#6668f0"
                          height="7"
                          rx="3.5"
                          width="94"
                          x="124"
                          y="37"
                        ></rect>
                        <text
                          fill="#2c2f46"
                          font-family="Figtree,Inter,Arial,sans-serif"
                          font-size="8.5"
                          font-weight="800"
                          x="124"
                          y="60"
                        >
                          Reading
                        </text>
                        <rect fill="#eceef3" height="7" rx="3.5" width="116" x="124" y="66"></rect>
                        <rect
                          class="ai-score-bar is-two"
                          fill="#9296f4"
                          height="7"
                          rx="3.5"
                          width="76"
                          x="124"
                          y="66"
                        ></rect>
                        <text
                          fill="#2c2f46"
                          font-family="Figtree,Inter,Arial,sans-serif"
                          font-size="8.5"
                          font-weight="800"
                          x="124"
                          y="89"
                        >
                          Writing
                        </text>
                        <rect fill="#eceef3" height="7" rx="3.5" width="116" x="124" y="95"></rect>
                        <rect
                          class="ai-score-bar is-three"
                          fill="#5a5cf0"
                          height="7"
                          rx="3.5"
                          width="86"
                          x="124"
                          y="95"
                        ></rect>
                      </g>
                    </svg>
                  </div>
                  <div class="ai-score-copy">
                    <h3>
                      <span class="ai-score-title-line">{{ $t('home_v2_ai_slide1_a') }}</span>
                      <span class="ai-score-title-accent">{{ $t('home_v2_ai_slide1_b') }}</span>
                    </h3>
                  </div>
                </article>
                <!-- 幻灯 2：学习诊断 -->
                <article class="ai-score-slide" :class="{ 'is-active': aiIndex === 1 }">
                  <div aria-hidden="true" class="ai-score-illustration">
                    <svg role="img" viewBox="21 20 278 124">
                      <g class="ai-score-visual-card" transform="translate(24 23)">
                        <rect
                          fill="#fff"
                          height="120"
                          rx="14"
                          stroke="#ebecef"
                          width="272"
                          x="0"
                          y="0"
                        ></rect>
                        <text
                          fill="#858994"
                          font-family="Figtree,Inter,Arial,sans-serif"
                          font-size="6.4"
                          font-weight="900"
                          letter-spacing=".65"
                          x="15"
                          y="18"
                        >
                          LEARNING DIAGNOSIS
                        </text>
                        <g class="ai-mastery-row" transform="translate(14 28)">
                          <rect
                            fill="#fafafd"
                            height="23"
                            rx="8"
                            stroke="#eceef3"
                            width="244"
                          ></rect>
                          <circle cx="16" cy="11.5" fill="#f1f0ff" r="7"></circle>
                          <text
                            fill="#6668f0"
                            font-family="Figtree,Inter,Arial,sans-serif"
                            font-size="6.2"
                            font-weight="900"
                            text-anchor="middle"
                            x="16"
                            y="14"
                          >
                            1
                          </text>
                          <text
                            fill="#2c2f46"
                            font-family="Figtree,Inter,Arial,sans-serif"
                            font-size="6.7"
                            font-weight="850"
                            x="29"
                            y="14"
                          >
                            Grammar
                          </text>
                          <rect fill="#e9eaf0" height="5" rx="2.5" width="60" x="142" y="9"></rect>
                          <rect
                            class="ai-mastery-bar"
                            fill="#6668f0"
                            height="5"
                            rx="2.5"
                            width="20"
                            x="142"
                            y="9"
                          ></rect>
                          <text
                            fill="#2c2f46"
                            font-family="Figtree,Inter,Arial,sans-serif"
                            font-size="8.7"
                            font-weight="900"
                            text-anchor="end"
                            x="229"
                            y="14.5"
                          >
                            33%
                          </text>
                        </g>
                        <g class="ai-mastery-row is-two" transform="translate(14 57)">
                          <rect
                            fill="#fafafd"
                            height="23"
                            rx="8"
                            stroke="#eceef3"
                            width="244"
                          ></rect>
                          <circle cx="16" cy="11.5" fill="#f1f1ff" r="7"></circle>
                          <text
                            fill="#9296f4"
                            font-family="Figtree,Inter,Arial,sans-serif"
                            font-size="6.2"
                            font-weight="900"
                            text-anchor="middle"
                            x="16"
                            y="14"
                          >
                            2
                          </text>
                          <text
                            fill="#2c2f46"
                            font-family="Figtree,Inter,Arial,sans-serif"
                            font-size="6.7"
                            font-weight="850"
                            x="29"
                            y="14"
                          >
                            Topic
                          </text>
                          <rect fill="#e9eaf0" height="5" rx="2.5" width="60" x="142" y="9"></rect>
                          <rect
                            class="ai-mastery-bar is-two"
                            fill="#9296f4"
                            height="5"
                            rx="2.5"
                            width="41"
                            x="142"
                            y="9"
                          ></rect>
                          <text
                            fill="#2c2f46"
                            font-family="Figtree,Inter,Arial,sans-serif"
                            font-size="8.7"
                            font-weight="900"
                            text-anchor="end"
                            x="229"
                            y="14.5"
                          >
                            68%
                          </text>
                        </g>
                        <g class="ai-mastery-row is-three" transform="translate(14 86)">
                          <rect
                            fill="#fafafd"
                            height="23"
                            rx="8"
                            stroke="#eceef3"
                            width="244"
                          ></rect>
                          <circle cx="16" cy="11.5" fill="#f3f3ff" r="7"></circle>
                          <text
                            fill="#5a5cf0"
                            font-family="Figtree,Inter,Arial,sans-serif"
                            font-size="6.2"
                            font-weight="900"
                            text-anchor="middle"
                            x="16"
                            y="14"
                          >
                            3
                          </text>
                          <text
                            fill="#2c2f46"
                            font-family="Figtree,Inter,Arial,sans-serif"
                            font-size="6.7"
                            font-weight="850"
                            x="29"
                            y="14"
                          >
                            Language Task
                          </text>
                          <rect fill="#e9eaf0" height="5" rx="2.5" width="60" x="142" y="9"></rect>
                          <rect
                            class="ai-mastery-bar is-three"
                            fill="#5a5cf0"
                            height="5"
                            rx="2.5"
                            width="25"
                            x="142"
                            y="9"
                          ></rect>
                          <text
                            fill="#2c2f46"
                            font-family="Figtree,Inter,Arial,sans-serif"
                            font-size="8.7"
                            font-weight="900"
                            text-anchor="end"
                            x="229"
                            y="14.5"
                          >
                            42%
                          </text>
                        </g>
                      </g>
                    </svg>
                  </div>
                  <div class="ai-score-copy">
                    <h3>
                      <span class="ai-score-title-line">{{ $t('home_v2_ai_slide2_a') }}</span>
                      <span class="ai-score-title-accent">{{ $t('home_v2_ai_slide2_b') }}</span>
                    </h3>
                  </div>
                </article>
                <!-- 幻灯 3：待复习字词 -->
                <article class="ai-score-slide" :class="{ 'is-active': aiIndex === 2 }">
                  <div aria-hidden="true" class="ai-score-illustration">
                    <svg role="img" viewBox="21 20 278 124">
                      <g class="ai-score-visual-card" transform="translate(24 23)">
                        <rect
                          fill="#fff"
                          height="120"
                          rx="14"
                          stroke="#ebecef"
                          width="272"
                          x="0"
                          y="0"
                        ></rect>
                        <g
                          v-for="word in aiReviewWords"
                          :key="word.hanzi"
                          :class="['ai-review-card', word.cls]"
                          :transform="word.transform"
                        >
                          <rect fill="#fff" height="42" rx="9" stroke="#eceef3" width="117"></rect>
                          <text
                            fill="#2c2f46"
                            font-family="Arial,'PingFang SC','Microsoft YaHei',sans-serif"
                            font-size="17"
                            font-weight="900"
                            x="12"
                            y="22"
                          >
                            {{ word.hanzi }}
                          </text>
                          <text
                            fill="#2c2f46"
                            font-family="Figtree,Inter,Arial,sans-serif"
                            font-size="6.8"
                            font-weight="850"
                            x="39"
                            y="15"
                          >
                            {{ word.pinyin }}
                          </text>
                          <text
                            fill="#8c909a"
                            font-family="Figtree,Inter,Arial,sans-serif"
                            font-size="5.5"
                            font-weight="700"
                            x="39"
                            y="25"
                          >
                            {{ word.meaning }}
                          </text>
                          <rect fill="#feebec" height="10" rx="5" width="29" x="78" y="8"></rect>
                          <text
                            fill="#fe848c"
                            font-family="Figtree,Inter,Arial,sans-serif"
                            font-size="4.8"
                            font-weight="900"
                            text-anchor="middle"
                            x="92.5"
                            y="14.8"
                          >
                            2 missed
                          </text>
                          <rect
                            fill="#e8e9ed"
                            height="3"
                            rx="1.5"
                            :width="word.bar"
                            x="39"
                            y="31"
                          ></rect>
                        </g>
                        <circle
                          class="ai-word-pointer"
                          cx="126"
                          cy="35"
                          fill="#6668f0"
                          r="3.5"
                        ></circle>
                      </g>
                    </svg>
                  </div>
                  <div class="ai-score-copy">
                    <h3>
                      <span class="ai-score-title-line">{{ $t('home_v2_ai_slide3_a') }}</span>
                      <span class="ai-score-title-accent">{{ $t('home_v2_ai_slide3_b') }}</span>
                    </h3>
                  </div>
                </article>
                <!-- 幻灯 4：学习建议 -->
                <article class="ai-score-slide" :class="{ 'is-active': aiIndex === 3 }">
                  <div aria-hidden="true" class="ai-score-illustration">
                    <svg role="img" viewBox="21 20 278 124">
                      <g class="ai-score-visual-card" transform="translate(24 23)">
                        <rect
                          fill="#fff"
                          height="120"
                          rx="14"
                          stroke="#ebecef"
                          width="272"
                          x="0"
                          y="0"
                        ></rect>
                        <text
                          fill="#858994"
                          font-family="Figtree,Inter,Arial,sans-serif"
                          font-size="6.4"
                          font-weight="900"
                          letter-spacing=".55"
                          x="15"
                          y="18"
                        >
                          NEXT STUDY RECOMMENDATIONS
                        </text>
                        <g
                          v-for="rec in aiRecommendations"
                          :key="rec.title"
                          :class="['ai-rec-card', rec.cls]"
                          :transform="rec.transform"
                        >
                          <rect
                            fill="#fafafd"
                            height="23"
                            rx="8"
                            stroke="#eceef3"
                            width="244"
                          ></rect>
                          <circle cx="16" cy="11.5" :fill="rec.circle" r="7"></circle>
                          <text
                            :fill="rec.color"
                            font-family="Figtree,Inter,Arial,sans-serif"
                            font-size="6.1"
                            font-weight="900"
                            text-anchor="middle"
                            x="16"
                            y="14"
                          >
                            {{ rec.no }}
                          </text>
                          <text
                            fill="#2c2f46"
                            :font-family="rec.titleFont"
                            font-size="7"
                            font-weight="850"
                            x="29"
                            y="9.6"
                          >
                            {{ rec.title }}
                          </text>
                          <text
                            fill="#8b8f99"
                            font-family="Figtree,Inter,Arial,sans-serif"
                            font-size="5.1"
                            font-weight="700"
                            x="29"
                            y="17.2"
                          >
                            {{ rec.sub }}
                          </text>
                          <rect
                            fill="#eceef3"
                            height="5"
                            rx="2.5"
                            width="38"
                            x="174"
                            y="8.8"
                          ></rect>
                          <rect
                            :class="['ai-rec-bar', rec.cls]"
                            :fill="rec.color"
                            height="5"
                            rx="2.5"
                            :width="rec.bar"
                            x="174"
                            y="8.8"
                          ></rect>
                          <rect fill="#feebec" height="11" rx="5.5" width="18" x="216" y="6"></rect>
                          <text
                            fill="#fe848c"
                            font-family="Figtree,Inter,Arial,sans-serif"
                            font-size="4.8"
                            font-weight="900"
                            text-anchor="middle"
                            x="225"
                            y="13.3"
                          >
                            {{ rec.pct }}
                          </text>
                        </g>
                      </g>
                    </svg>
                  </div>
                  <div class="ai-score-copy">
                    <h3>
                      <span class="ai-score-title-line">{{ $t('home_v2_ai_slide4_a') }}</span>
                      <span class="ai-score-title-accent">{{ $t('home_v2_ai_slide4_b') }}</span>
                    </h3>
                  </div>
                </article>
              </div>
            </div>
            <div class="ai-score-carousel-nav" role="tablist" aria-label="AI Score Report features">
              <button
                v-for="(label, i) in aiSlideLabels"
                :key="label"
                class="ai-carousel-dot"
                :class="{ 'is-active': aiIndex === i }"
                :aria-label="label"
                :aria-selected="aiIndex === i ? 'true' : 'false'"
                type="button"
                @click="onAiDotClick(i)"
              ></button>
            </div>
          </div>
          <div class="ai-score-footer">
            <button class="btn btn-primary" type="button" @click="goChooseExam('hsk_levels_tri')">
              {{ $t('home_v2_ai_cta') }}
            </button>
            <span class="ai-score-note">{{ $t('home_v2_ai_note') }}</span>
          </div>
        </div>
      </section>

      <!-- Learner results：内容仍来自 bannerText 接口（title = 学员名，text = 评价），跑马灯按原型 -->
      <section class="home-section is-soft testimonials-section" id="learnerResults">
        <div class="container">
          <div class="home-heading testimonials-heading">
            <h2>{{ $t('home_v2_testimonials_title') }}</h2>
            <p>{{ $t('home_v2_testimonials_desc') }}</p>
          </div>
          <div
            v-if="banners && banners.length"
            class="testimonials-rail"
            ref="testimonialRail"
            aria-label="Learner testimonials"
            @mouseenter="pauseTestimonials"
            @mouseleave="resumeTestimonials"
            @focusin="pauseTestimonials"
            @focusout="onTestimonialFocusOut"
          >
            <div class="testimonials-track" ref="testimonialTrack">
              <div
                v-for="groupIndex in 2"
                :key="groupIndex"
                class="testimonials-group"
                :aria-hidden="groupIndex === 2 ? 'true' : null"
              >
                <article
                  v-for="(item, index) in banners"
                  :key="groupIndex + '-' + index"
                  class="testimonial-card"
                  :class="{ 'is-centre-card': centreCardKey === groupIndex + ':' + index }"
                >
                  <blockquote>{{ item.text }}</blockquote>
                  <div class="testimonial-footer">
                    <div class="testimonial-person">
                      <img class="testimonial-avatar-photo" alt="" :src="getImagePath(index)" />
                      <div>
                        <strong>{{ item.title }}</strong>
                      </div>
                    </div>
                  </div>
                </article>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Practise the HSK online（experience v32：文案叠层 + 笔记本屏幕轮播） -->
      <section class="experience-section experience-v32" id="whyHskMock" ref="experienceSection">
        <div class="container">
          <div class="experience-heading experience-v32-heading">
            <h2>{{ $t('home_v2_experience_title') }}</h2>
          </div>
          <div class="experience-v32-showcase">
            <div class="experience-v32-copy-stack" aria-live="polite">
              <article
                v-for="(item, i) in experienceItems"
                :key="item.key"
                class="experience-v32-copy"
                :class="{ 'is-active': experienceIndex === i }"
                :aria-hidden="experienceIndex === i ? 'false' : 'true'"
              >
                <h3>
                  <span>{{ $t(item.titleA) }}</span>
                  <span class="experience-v32-accent">{{ $t(item.titleB) }}</span>
                </h3>
                <p>{{ $t(item.desc) }}</p>
              </article>
            </div>
            <div
              class="experience-v32-visual"
              aria-label="HSK Mock platform shown on a laptop"
              @pointerdown="onExperiencePointerDown"
              @pointerup="onExperiencePointerUp"
              @pointercancel="experienceSwipeStartX = null"
            >
              <div class="experience-v32-laptop">
                <img
                  class="experience-v32-laptop-frame"
                  alt=""
                  aria-hidden="true"
                  :src="require('@/assets/images/home/v2/laptop-frame.png')"
                />
                <div class="experience-v32-screen">
                  <!--
                    轨道位移必须用 setProperty(..., 'important')：home-v2.scss 里
                    .experience-v32-screen-track 写了 transform:!important，普通 :style 盖不过去，
                    否则永远停在第一张（与原型 HSK-Mock-Prototype 一致）。
                  -->
                  <div ref="experienceTrack" class="experience-v32-screen-track">
                    <figure
                      v-for="item in experienceItems"
                      :key="item.key"
                      class="experience-v32-screen-panel"
                    >
                      <img :alt="$t(item.imageAlt)" :src="item.image" />
                    </figure>
                  </div>
                </div>
              </div>
              <div class="experience-v32-dots" aria-hidden="true">
                <span
                  v-for="(item, i) in experienceItems"
                  :key="item.key"
                  :class="{ 'is-active': experienceIndex === i }"
                ></span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Schools & institutions：CTA 仍走 onClickInstitutional（/solutions） -->
      <section class="b2b-v41 b2b-v42" id="institutions">
        <div class="container">
          <div class="experience-heading b2b-v41-heading">
            <h2>{{ $t('home_v2_b2b_title') }}</h2>
          </div>
          <div class="b2b-v41-showcase">
            <div class="b2b-v41-copy b2b-v53-copy">
              <p class="b2b-v53-lead">{{ $t('home_v2_b2b_lead') }}</p>
              <div
                class="b2b-v41-points b2b-v53-points"
                aria-label="Institutional HSK Mock features"
              >
                <span>{{ $t('home_v2_b2b_point1') }}</span>
                <span>{{ $t('home_v2_b2b_point2') }}</span>
                <span>{{ $t('home_v2_b2b_point3') }}</span>
              </div>
              <button
                class="btn btn-primary b2b-v42-cta"
                type="button"
                @click="onClickInstitutional"
              >
                {{ $t('home_v2_b2b_cta') }}
              </button>
            </div>
            <figure class="b2b-v41-visual">
              <div class="b2b-v41-photo-frame">
                <img
                  :alt="$t('home_v2_b2b_image_alt')"
                  :src="require('@/assets/images/home/v2/institutions-classroom.png')"
                />
              </div>
            </figure>
          </div>
        </div>
      </section>

      <!-- Mobile app：两个商店按钮仍走 handleClick（原有埋点 + 国内外链接分流） -->
      <section class="app-brand-section app-brand-v19" id="mobileApp">
        <div class="container app-brand-layout">
          <div class="app-brand-copy">
            <h2>{{ $t('home_v2_app_title') }}</h2>
            <p>{{ $t('home_v2_app_desc') }}</p>
            <div class="store-buttons">
              <button
                class="store-button store-button-badge"
                type="button"
                :aria-label="$t('home_v2_app_store_aria')"
                @click="handleClick('app-store')"
              >
                <img
                  class="store-badge-img store-badge-apple"
                  alt=""
                  width="120"
                  height="40"
                  decoding="async"
                  :src="require('@/assets/images/home/v2/store-badges/app-store-black.svg')"
                />
              </button>
              <button
                class="store-button store-button-badge"
                type="button"
                :aria-label="$t('home_v2_google_play_aria')"
                @click="handleClick('google-play')"
              >
                <img
                  class="store-badge-img store-badge-google"
                  alt=""
                  width="135"
                  height="40"
                  decoding="async"
                  :src="require('@/assets/images/home/v2/store-badges/google-play-badge.svg')"
                />
              </button>
            </div>
          </div>
          <div class="app-visual-collage" aria-label="HSK Mock on desktop and mobile">
            <span aria-hidden="true" class="app-o-v19"></span>
            <figure class="app-visual-photo app-visual-learner">
              <img
                :alt="$t('home_v2_app_learner_alt')"
                :src="require('@/assets/images/home/v2/app-learner.webp')"
              />
            </figure>
            <figure class="app-visual-photo app-visual-phone">
              <img
                :alt="$t('home_v2_app_phone_alt')"
                :src="require('@/assets/images/home/v2/app-phone.webp')"
              />
            </figure>
          </div>
        </div>
      </section>

      <!-- Need help：FAQ 去博客静态页（同 Header），Contact 去 /feedback（同 Header） -->
      <section class="home-section is-white simple-help-section" id="help">
        <div class="container simple-help-layout">
          <div class="simple-help-main">
            <div class="home-heading simple-help-heading-main">
              <h2>{{ $t('home_v2_help_title') }}</h2>
            </div>
            <div class="help-grid help-grid-two">
              <a class="help-card" :href="faqHref" :aria-label="$t('home_v2_help_faq_link')">
                <div class="help-card-title-row">
                  <span aria-hidden="true" class="help-icon">
                    <svg viewBox="0 0 24 24">
                      <circle
                        cx="12"
                        cy="12"
                        fill="none"
                        r="8.5"
                        stroke="currentColor"
                        stroke-width="1.7"
                      ></circle>
                      <path
                        d="M9.8 9.4a2.4 2.4 0 1 1 3.9 1.9c-.9.7-1.7 1.1-1.7 2.4"
                        fill="none"
                        stroke="currentColor"
                        stroke-linecap="round"
                        stroke-width="1.7"
                      ></path>
                      <circle cx="12" cy="16.9" fill="currentColor" r=".9"></circle>
                    </svg>
                  </span>
                  <strong>{{ $t('home_v2_help_faq') }}</strong>
                </div>
                <span>{{ $t('home_v2_help_faq_desc') }}</span>
                <span class="help-link">{{ $t('home_v2_help_faq_link') }}</span>
              </a>
              <button class="help-card" type="button" @click="goContact">
                <div class="help-card-title-row">
                  <span aria-hidden="true" class="help-icon">
                    <svg viewBox="0 0 24 24">
                      <path
                        d="M5 5.5h14v10H9.2L5 19V5.5z"
                        fill="none"
                        stroke="currentColor"
                        stroke-linejoin="round"
                        stroke-width="1.7"
                      ></path>
                      <path
                        d="M8 9h8M8 12h5"
                        fill="none"
                        stroke="currentColor"
                        stroke-linecap="round"
                        stroke-width="1.7"
                      ></path>
                    </svg>
                  </span>
                  <strong>{{ $t('home_v2_help_contact') }}</strong>
                </div>
                <span>{{ $t('home_v2_help_contact_desc') }}</span>
                <span class="help-link">{{ $t('home_v2_help_contact_link') }}</span>
              </button>
            </div>
          </div>
          <div class="simple-help-mascot">
            <div class="mascot-speech-bubble">
              <h2>{{ $t('home_v2_help_bubble') }}</h2>
            </div>
            <span aria-hidden="true" class="simple-help-orbit"></span>
            <img
              :alt="$t('home_v2_help_mascot_alt')"
              :src="require('@/assets/images/home/v2/help-panda.webp')"
            />
          </div>
        </div>
      </section>
    </div>

    <!-- 以下弹窗是原有业务：免费水平自测 / 移动端引导下载 App / 自由练习。UI 改版不动它们 -->
    <DialogTest :visible="centerDialogVisible" @update:visible="dialogUpdate"></DialogTest>
    <el-dialog
      width="95%"
      :show-close="false"
      :visible.sync="dialogNeedApp"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      class="practice-root"
    >
      <NeedDownload @close-modal="dialogNeedApp = false" />
    </el-dialog>
    <el-dialog
      :visible.sync="freePracticeDialogVisible"
      :width="isMobile ? '90%' : '1024px'"
      :show-close="false"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      class="practice-root"
      custom-class="free-practice-dialog"
    >
      <FreePractice
        v-if="freePracticeDialogVisible"
        :initial-type="freePracticeType"
        :initial-level="freePracticeLevel"
        @closeDialog="freePracticeDialogVisible = false"
      ></FreePractice>
    </el-dialog>
  </div>
</template>

<script>
import analytics from '@/utils/AnalyticsCenter'
import { mixinMixpanel } from '@/mixins'
import { mapGetters, mapState } from 'vuex'
import { IS_CHINA } from '@/config'
import 'swiper/css/swiper.css'
import Swiper from 'swiper'
import axios from 'axios'
import { apiRequest } from '@/serves/http'
import { getToken } from '@/utils/cache'
import ApiPath from '@/serves/path'
// 在线视频播放组件
import NeedDownload from '@/views/hsk/components/need-download-app.vue'
import DialogTest from '@/components/DialogTest'
import FreePractice from '@/views/hsk/components/freePractice'
import { jumpToHankao } from '@/utils/utils'
import { isMobileUA } from '@/utils/browser'
import { getFaqHref } from '@/utils/faqSite'
export default {
  name: 'HomeView',
  components: {
    NeedDownload,
    DialogTest,
    FreePractice,
  },
  mixins: [mixinMixpanel],
  data() {
    return {
      // ---- 首页改版（UI）：以下状态只服务于原型的展示与动效 ----
      // 五个考试入口 → 等级选择页对应 tab（key 与 ChooseLevel/index.vue 的 TABS 一致，通过 ?tab= 带过去）
      examOptions: [
        { tab: 'hsk_levels_tri', labelKey: 'hsk_levels_tri' },
        { tab: 'hsk_speaking_tri', labelKey: 'hsk_speaking_tri' },
        { tab: 'yct', labelKey: 'yct' },
        { tab: 'hsk_levels', labelKey: 'hsk_levels' },
        { tab: 'hsk_speaking', labelKey: 'hsk_speaking' },
      ],
      // AI Score Report 轮播
      aiIndex: 0,
      aiScoreDisplay: '0',
      aiPassedVisible: false,
      aiActivated: false,
      aiTimer: null,
      aiRaf: null,
      aiCountDelay: null,
      // 插图里的示意数据（原型写死的演示内容，aria-hidden）
      aiReviewWords: [
        { hanzi: '后', pinyin: 'hòu', meaning: 'after · behind', cls: '', transform: 'translate(14 14)', bar: 58 },
        { hanzi: '在', pinyin: 'zài', meaning: 'at · in', cls: 'is-two', transform: 'translate(141 14)', bar: 52 },
        { hanzi: '看', pinyin: 'kàn', meaning: 'look · watch', cls: 'is-three', transform: 'translate(14 64)', bar: 61 },
        { hanzi: '边', pinyin: 'biān', meaning: 'side · edge', cls: 'is-four', transform: 'translate(141 64)', bar: 55 },
      ],
      aiRecommendations: [
        { no: 1, title: 'Science & Technology', sub: 'Strengthen this topic', bar: 24, pct: '62%', color: '#6668f0', circle: '#f1f0ff', cls: '', transform: 'translate(14 28)', titleFont: 'Figtree,Inter,Arial,sans-serif' },
        { no: 2, title: 'Time expressions', sub: '年 · 月 · 日 · 星期', bar: 10, pct: '25%', color: '#9296f4', circle: '#f1f1ff', cls: 'is-two', transform: 'translate(14 57)', titleFont: 'Figtree,Inter,Arial,sans-serif' },
        { no: 3, title: '乘客 · 用 · 下单 · 个性', sub: 'Review meanings, usage and context', bar: 23, pct: '60%', color: '#5a5cf0', circle: '#f3f3ff', cls: 'is-three', transform: 'translate(14 86)', titleFont: "Arial,'PingFang SC','Microsoft YaHei',sans-serif" },
      ],
      // Practise the HSK Online：笔记本屏幕轮播
      experienceIndex: 0,
      experienceTimer: null,
      experienceVisible: true,
      experienceSwipeStartX: null,
      experienceItems: [
        { key: 'interface', titleA: 'home_v2_exp1_a', titleB: 'home_v2_exp1_b', desc: 'home_v2_exp1_desc', imageAlt: 'home_v2_exp1_alt', image: require('@/assets/images/home/v2/screen-test-interface.webp') },
        { key: 'score', titleA: 'home_v2_exp2_a', titleB: 'home_v2_exp2_b', desc: 'home_v2_exp2_desc', imageAlt: 'home_v2_exp2_alt', image: require('@/assets/images/home/v2/screen-scoring.jpg') },
        { key: 'vocab', titleA: 'home_v2_exp3_a', titleB: 'home_v2_exp3_b', desc: 'home_v2_exp3_desc', imageAlt: 'home_v2_exp3_alt', image: require('@/assets/images/home/v2/screen-vocabulary.webp') },
      ],
      // 评价跑马灯
      testimonialAnimation: null,
      testimonialPaused: false,
      testimonialResizeTimer: null,
      centreCardIndex: -1,
      // 当前居中卡片：'组下标:卡片下标'
      centreCardKey: '',
      centreCardTimer: null,
      // 各类观察者，销毁时统一断开
      homeObservers: [],
      // 可信度要点入场用的 requestAnimationFrame 句柄
      proofFrame: null,
      // 首次渲染即取正确值，避免移动端先渲染 PC 版再切换造成布局跳变（CLS）
      isMobile: isMobileUA(),
      swiper: null,
      mySwiper: null,
      modileSwiper: null,
      articleList: [],
      dialogNeedApp: false, // 移动端下载App弹窗
      centerDialogVisible: false, // 免费等级测试弹窗
      freePracticeDialogVisible: false, // 自由练习弹窗
      freePracticeType: '', // 自由练习类型：FLASHCARDS, EXERCISE, ANALYSE
      freePracticeLevel: '', // 自由练习等级：hsk1, hsk2, ...
      confidenceList: [
        {
          icon: require('@/assets/images/home/ready-2.svg'),
          title: this.$t('real_hsk_exam_content_new'),
          desc: this.$t('real_hsk_exam_desc_new'),
        },
        {
          icon: require('@/assets/images/home/ready-1.svg'),
          title: this.$t('official_level_scoring_new'),
          desc: this.$t('official_level_scoring_desc'),
        },
        {
          icon: require('@/assets/images/home/ready-3.svg'),
          title: this.$t('vocabulary_boost_new'),
          desc: this.$t('vocabulary_boost_desc_new'),
        },
      ],
      howWorkList: [
        {
          title: this.$t('choose_your_level'),
          desc: this.$t('choose_your_level_desc'),
        },
        {
          title: this.$t('practise_and_learn'),
          desc: this.$t('practise_and_learn_desc'),
        },
        {
          title: this.$t('track_your_progress'),
          desc: this.$t('track_your_progress_desc'),
        },
      ],
      answersList: [
        {
          icon: require('@/assets/images/home/answer-1.svg'),
          title: this.$t('faq'),
          desc: this.$t('faq_desc'),
          link: '/index.html',
          isExternal: true,
          btnStr: this.$t('faq_btn'),
        },
        {
          icon: require('@/assets/images/home/answer-2.svg'),
          title: this.$t('blog'),
          desc: this.$t('blog_desc'),
          link: '/index.html',
          isExternal: true,
          btnStr: this.$t('blog_btn'),
        },
        {
          icon: require('@/assets/images/home/answer-3.svg'),
          title: this.$t('contact'),
          desc: this.$t('contact_desc'),
          link: '/feedback',
          btnStr: this.$t('contact_btn'),
        },
      ],
      bgImages: [
        require('@/assets/images/home/mobile-home-bg-1.png'),
        require('@/assets/images/home/mobile-home-bg-2.png'),
        require('@/assets/images/home/mobile-home-bg-3.png'),
      ], // 背景图片路径数组
      bgImageIndex: 0, // 当前背景图片索引
      bgImageUrl: require('@/assets/images/home/home-header-new.png'),
      timer: null,
      banners: [],
      activeCardIndex: 0,
      localAvatars: [
        'feedback-photo-1.png',
        'feedback-photo-2.png',
        'feedback-photo-3.png',
        'feedback-photo-4.png',
        'feedback-photo-5.png',
        'feedback-photo-6.png',
        'feedback-photo-7.png',
        'feedback-photo-8.png',
        'feedback-photo-9.png',
      ],
      institutionalImages: [
        require('@/assets/images/home/institutional-1.png'),
        require('@/assets/images/home/institutional-2.png'),
        require('@/assets/images/home/institutional-3.png'),
      ],
      currentInstitutionalSlide: 0,
      institutionalTimer: null,
      selectedLevel: '',
      selectedMethod: 'word_practice',
      practiceMethodList: [
        { key: 'word_flashcards', label: this.$t('word_flashcards') },
        { key: 'word_practice', label: this.$t('word_practice') },
        { key: 'analyse_text', label: this.$t('analyse_text') },
      ],
    }
  },
  watch: {
    isMobile: {
      handler(val) {
        if (val) {
          if (this.banners.length > 0) {
            this.$nextTick(this.initTestimonialMarquee)
          }
        } else {
          clearInterval(this.timer)
          if (this.banners.length > 0) {
            this.$nextTick(this.initTestimonialMarquee)
          }
          if (this.articleList.length > 0) {
            this.initMySwiper()
          }
        }
      },
      immediate: true,
    },
    // 当 hskLevels 有数据时，设置默认选中用户当前等级或第一项
    hskLevels: {
      handler(val) {
        if (val && val.length > 0 && !this.selectedLevel) {
          // 优先使用用户当前选中的等级
          if (this.currentUserLevelName && val.includes(this.currentUserLevelName)) {
            this.selectedLevel = this.currentUserLevelName
          } else {
            this.selectedLevel = val[0]
          }
        }
      },
      immediate: true,
    },
    // 当用户等级变化时，更新选中状态
    currentUserLevelName: {
      handler(val) {
        if (val && this.hskLevels.includes(val)) {
          this.selectedLevel = val
        }
      },
    },
  },
  mounted() {
    // console.log(sentryerrornew)
    // UI 改版：启动原型的滚动显现 / AI 轮播 / 屏幕轮播等纯展示行为
    this.$nextTick(this.initHomeV2Motion)
  },
  beforeMount() {
    this.$_resizeHandler()
    window.addEventListener('resize', this.$_resizeHandler)
    clearInterval(this.timer)
  },
  created() {
    // 首页埋点
    analytics.logEvent('hskweb_official_view')
    this.getBannerText()
    this.startInstitutionalSwiper()
  },
  computed: {
    // 首页改版：FAQ 与 Header 里「Blog & FAQ」同一去向（按当前语言进静态站对应目录）
    faqHref() {
      return getFaqHref(this.$i18n.locale)
    },
    aiSlideLabels() {
      return [1, 2, 3, 4].map((n) => this.$t(`home_v2_ai_slide${n}_a`) + ' ' + this.$t(`home_v2_ai_slide${n}_b`))
    },
    reduceMotion() {
      return typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    },
    isChina() {
      return IS_CHINA
    },
    langTitle() {
      return (lang) => {
        if (this.$i18n.locale != 'en') {
          return (
            lang
              .split('[:')
              .find((item) => item.startsWith('zh'))
              ?.split(']')?.[1] || ''
          )
        } else {
          return (
            lang
              .split('[:')
              .find((item) => item.startsWith(this.$i18n.locale))
              ?.split(']')?.[1] || ''
          )
        }
      }
    },
    ...mapGetters('level', {
      levelTypeName: 'levelTypeName',
    }),
    ...mapState({
      // 等级信息
      levelActiveIndex: (state) => state.level.levelActiveIndex,
      levelActive: (state) => state.level.levelActive,
      levelList: (state) => state.level.levelList,
      isGrading: (state) => state.grade.isGrading,
    }),
    // 从接口返回的第一组数据中提取等级列表
    hskLevels() {
      if (!this.levelList || this.levelList.length === 0) return []
      // 取第一组数据（通常是HSK）
      const firstGroup = this.levelList[0]
      if (!firstGroup || !firstGroup.levels) return []
      // 返回等级名称数组
      return firstGroup.levels.map((item) => item.level_name)
    },
    // 获取当前用户选中的等级名称
    currentUserLevelName() {
      if (!this.levelList || this.levelList.length === 0) return ''
      if (!this.levelActive) return ''
      // 在第一组数据中查找当前选中的等级
      const firstGroup = this.levelList[0]
      if (!firstGroup || !firstGroup.levels) return ''
      const currentLevel = firstGroup.levels.find((item) => item.id == this.levelActive)
      return currentLevel ? currentLevel.level_name : ''
    },
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.$_resizeHandler)
    this.stopInstitutionalSwiper()
    this.teardownHomeV2()
  },
  methods: {
    getImagePath(index) {
      return require(`@/assets/images/home/${this.localAvatars[index % this.localAvatars.length]}`)
    },
    // 点击考试日期按钮
    onClickDate() {
      analytics.logEvent('hskweb_checktestdates_click')
      jumpToHankao(true)
    },
    // 点击报名考试按钮
    onClickJoin() {
      analytics.logEvent('hskweb_bookatest_click')
      jumpToHankao(false)
    },
    // 计算当前评论id集合 共三个
    getReviewIds() {
      let total = this.banners.length
      if (!total || total <= 0) {
        // 无数据
        return []
      }
      // 当前值最小为 1 最大应为数组长度， 但是实际上会超上限 例如total=9 max会为10 11，存在两个超上限值
      // 评价区已不用 Swiper：取跑马灯当前居中卡片的下标。旧 Swiper(loop) 的 activeIndex 从 1 起，这里 +1 保持埋点口径不变
      if (typeof this.centreCardIndex !== 'number' || this.centreCardIndex < 0) return []
      let current = this.centreCardIndex + 1
      if (current > total) current -= total
      let first = current - 1
      if (first < 0) first = total - 1
      let third = current + 1
      if (third >= total) third = 0
      console.log('当前评论集合', first, current, third)
      return [first, current, third]
    },
    // 点击使用移动端
    onClickMoreHsk() {
      analytics.logEvent('hskweb_intromore_click', { type: 'hsk' })
    },
    // 点击 YCT 更多按钮
    onClickMoreYct() {
      analytics.logEvent('hskweb_intromore_click', { type: 'yct' })
    },
    // 点击评论翻页左箭头
    onClickLeftArrow() {
      analytics.logEvent('hskweb_reviewswitch_click', {
        direction: 'left',
      })
    },
    // 点击评论翻页右箭头
    onClickRightArrow() {
      analytics.logEvent('hskweb_reviewswitch_click', {
        direction: 'right',
      })
    },
    // 点击首屏 Hero 区"开始练习"按钮 - 手机端不弹窗，直接跳转
    onClickHeroStart() {
      analytics.logEvent('hskweb_getstarted_click', {
        position: 'above_the_fold',
        banner_text_ids: this.getReviewIds(),
      })
      this.goCheckLevel(true)
    },
    // 点击 How it Works 区"开始"按钮 - 手机端不弹窗，直接跳转
    onClickHowItWorksStart() {
      analytics.logEvent('hskweb_getstarted_click', {
        position: 'above_the_fold',
        banner_text_ids: this.getReviewIds(),
      })
      this.goCheckLevel(true)
    },
    // 点击卡片0详情区"免费水平自测"按钮 - 手机端弹窗下载App
    onClickCard0LevelTest() {
      analytics.logEvent('hskweb_getstarted_click', {
        position: 'above_the_fold',
        banner_text_ids: this.getReviewIds(),
      })
      const isMobile = document.body.getBoundingClientRect().width - 1 < 768
      if (isMobile) {
        this.dialogNeedApp = true
        return
      }
      if (this.isGrading) {
        this.$router.push({ name: 'levelReport' })
      } else {
        this.centerDialogVisible = true
      }
    },
    // 点击卡片1"模考"按钮 - 手机端弹窗下载App，PC端跳转并使用选中的等级
    onClickCard1MockTest() {
      analytics.logEvent('hskweb_getstarted_click', {
        position: 'above_the_fold',
        banner_text_ids: this.getReviewIds(),
      })
      const isMobile = document.body.getBoundingClientRect().width - 1 < 768
      if (isMobile) {
        this.dialogNeedApp = true
        return
      }
      // PC端：使用选中的等级进行跳转
      this.goCheckLevelWithSelected(this.selectedLevel)
    },
    // 点击 Banner 评论区"开始学习"按钮 - 手机端弹窗下载App
    onClickBannerStart() {
      analytics.logEvent('hskweb_getstarted_click', {
        position: 'review',
        banner_text_ids: this.getReviewIds(),
      })
      this.goCheckLevel()
    },
    // 点击词汇练习方法按钮（单词闪卡、单词练习、文本分析）
    onClickPracticeMethod(methodKey) {
      analytics.logEvent('hskweb_practice_method_click', {
        method: methodKey,
      })
      // 移动端弹窗提示下载App
      if (this.isMobile) {
        this.dialogNeedApp = true
        return
      }
      // 将 methodKey 转换为自由练习组件的 key 格式
      const methodMap = {
        word_flashcards: 'FLASHCARDS',
        word_practice: 'EXERCISE',
        analyse_text: 'ANALYSE',
      }
      this.freePracticeType = methodMap[methodKey]
      // 将选中的等级名称转换为小写格式（如 HSK1 -> hsk1）
      this.freePracticeLevel = this.selectedLevel ? this.selectedLevel.toLowerCase() : ''
      this.freePracticeDialogVisible = true
    },
    goCheckLevel(skipMobileCheck = false) {
      const isMobile = document.body.getBoundingClientRect().width - 1 < 768
      if (isMobile && !skipMobileCheck) {
        this.dialogNeedApp = true
        return
      }
      // 需要判断如果存储有误，更新存储，以LevelActive为准；
      if (this.$store.state.user.login && getToken()) {
        if (this.$store.state.level.levelId) {
          console.log('levelActiveIndex', this.levelActiveIndex)
          console.log(this.$store.state.level.levelId, 'this.$store.state.level.levelId')
          console.log(this.levelTypeName, 'this.$store.state.level.levelId')
          if (this.levelTypeName == 'hsk-3') {
            this.$router.push({
              path: `/new-hsk/tests`,
              query: {
                level: `${this.levelTypeName}${this.levelActiveIndex}`,
              },
            })
          } else {
            this.$router.push({
              path: `/${this.levelTypeName || 'hsk'}/tests`,
              query: {
                level: `${this.levelTypeName}${this.levelActiveIndex}`,
              },
            })
          }

        } else {
          this.$router.push({ path: '/chooselevel/index' })
        }
      } else {
        this.$router.push({ path: '/chooselevel/index' })
      }
    },
    // 使用选中的等级进行跳转（用于卡片1模考按钮）
    goCheckLevelWithSelected(selectedLevel) {
      // 将选中的等级转换为小写格式用于跳转
      const levelQuery = selectedLevel ? selectedLevel.toLowerCase() : ''
      if (this.$store.state.user.login && getToken()) {
        if (levelQuery) {
          // 根据等级判断是 HSK 3.0 还是普通 HSK
          if (levelQuery.includes('hsk-3')) {
            this.$router.push({
              path: `/new-hsk/tests`,
              query: {
                level: levelQuery,
              },
            })
          } else {
            this.$router.push({
              path: `/${levelQuery.startsWith('hskk') ? 'hskk' : 'hsk'}/tests`,
              query: {
                level: levelQuery,
              },
            })
          }
        } else {
          // 没有选中等级，跳转到选择等级页
          this.$router.push({ path: '/chooselevel/index' })
        }
      } else {
        // 未登录，跳转到选择等级页
        this.$router.push({ path: '/chooselevel/index' })
      }
    },
    checkForDuplicates(existingList, avatar) {
      if (existingList.length < 2) {
        return false
      }
      const lastIndex = existingList.length - 1
      const secondLastIndex = existingList.length - 2
      return (
        existingList[lastIndex].avatar === avatar && existingList[secondLastIndex].avatar === avatar
      )
    },

    $_resizeHandler() {
      // 必须同步赋值：延后到宏任务会让首屏先按 PC 版绘制再切换，造成布局跳变（CLS）
      this.isMobile = isMobileUA()
      if (!this.isMobile && this.timer) {
        clearInterval(this.timer)
      }
    },
    handleClick(key) {
      // TODO：关于我们、意见反馈、app下载地址、copyright
      let mixpanelParams = { position: 'homepage' }
      if (key === 'android') {
        //安卓下载埋点
        analytics.logEvent('hskweb_android_click', mixpanelParams)
        window.open(
          this.isChina
            ? process.env.VUE_APP_MODE == 'prod'
              ? `${this.$store.state.publicUrl.mockPublicUrl}/static/android/com.wohui.mocktest.china-mockChina-prod.apk`
              : `${this.$store.state.publicUrl.mockPublicUrl}/static/android/com.wohui.mocktest.china-mockChina-test.apk`
            : process.env.VUE_APP_MODE == 'prod'
              ? `${this.$store.state.publicUrl.mockPublicUrl}/static/android/com.wohui.mocktest-foreign-prod.apk`
              : `${this.$store.state.publicUrl.mockPublicUrl}/static/android/com.wohui.mocktest-foreign-test.apk`
        )
      } else if (key === 'app-store') {
        //苹果下载埋点
        analytics.logEvent('hskweb_apple_click', mixpanelParams)
        window.open(
          this.isChina
            ? 'https://apps.apple.com/cn/app/hsk-mock-china/id6453362725'
            : 'https://apps.apple.com/cn/app/hsk-mock/id1624661903'
        )
      } else if (key === 'google-play') {
        //谷歌下载埋点
        analytics.logEvent('hskweb_google_click', mixpanelParams)
        window.open('https://play.google.com/store/apps/details?id=com.wohui.mocktest')
      }
    },
    getWohuiBanner() {
      //?category=63&post_status=publish
      axios
        .get(
          'https://articles.wohuimandarin.com/wp-json/wp/v2/posts?category=63&post_status=publish&numberposts=3&offset=0&post_type=post',
          {
            timeout: 60000,
          }
        )
        .then((res) => {
          this.articleList = res.data?.slice(0, 3) || []
          this.initMySwiper()
        })
    },
    getBannerTransferLanguage(transferStr) {
      if (!transferStr) {
        return ''
      }
      let globalLanguage = localStorage.getItem('language')
      let language = 'en'
      if (globalLanguage == 'zh' || globalLanguage == 'cn') {
        language = 'zh'
      }
      return JSON.parse(transferStr)[language]
    },
    getBannerText() {
      apiRequest.get(ApiPath.bannerText).then((res) => {
        this.banners = res.data.banners || []
        this.banners.forEach((item) => {
          console.log(item)
          item.text = this.getBannerTransferLanguage(item.text_trans)
          item.title = this.getBannerTransferLanguage(item.title_trans)
        })
        // UI 改版：评价区由 Swiper 轮播改为原型的无缝跑马灯（内容仍来自本接口）
        this.$nextTick(this.initTestimonialMarquee)
      })
    },
    initSwiperMobile() {
      this.swiper = null
      this.$nextTick(() => {
        this.modileSwiper = new Swiper('.swiper-container-mobile', {
          slidesPerView: 1,
          watchSlidesProgress: true,
          spaceBetween: 20,
          centeredSlides: false,
          loop: true,
          autoplay: {
            delay: 3000,
            disableOnInteraction: false,
          },
          pagination: {
            el: '.banner-pagination',
          },
        })
      })
    },
    initBannerContainer() {
      this.swiper = null
      this.$nextTick(() => {
        // 向上取整
        const count = (window.document.body.clientWidth / 357).toFixed(0)
        console.log('count', count)
        this.modileSwiper = new Swiper('.banner-container', {
          slidesPerView: count,
          centeredSlides: true, // 设定为true时，active slide会居中，而不是默认状态下的居左。
          spaceBetween: 20,
          loop: true,
          autoplay: {
            delay: 3000,
          },
          navigation: {
            nextEl: '.banner-button-next',
            prevEl: '.banner-button-prev',
          },
        })

        this.modileSwiper.el.addEventListener(
          'mouseenter',
          function () {
            this.swiper.autoplay.stop()
          },
          false
        )

        this.modileSwiper.el.addEventListener(
          'mouseleave',
          function () {
            this.swiper.autoplay.start()
          },
          false
        )
      })
    },
    initMySwiper() {
      this.$nextTick(() => {
        this.mySwiper = new Swiper('.mySwiper', {
          loop: true,
          slidesPerView: 3,
          spaceBetween: 100,
          navigation: {
            nextEl: '.mySwiper-button-next',
            prevEl: '.mySwiper-button-prev',
          },
        })
      })
    },
    onClickPartner() {
      console.log('点击进入表单')
      this.$router.push({ path: '/partnerforms/index' })
    },
    handleCardClick(index) {
      this.activeCardIndex = index
    },
    onClickInstitutional() {
      this.$router.push({ path: '/solutions' })
    },
    // 弹窗显示状态发生变化
    dialogUpdate(val) {
      this.centerDialogVisible = val
    },
    startInstitutionalSwiper() {
      if (!this.institutionalImages || this.institutionalImages.length === 0) return
      this.institutionalTimer = setInterval(() => {
        this.currentInstitutionalSlide = (this.currentInstitutionalSlide + 1) % this.institutionalImages.length
      }, 3000)
    },
    stopInstitutionalSwiper() {
      if (this.institutionalTimer) {
        clearInterval(this.institutionalTimer)
        this.institutionalTimer = null
      }
    },
    // ======== 首页改版（UI）：以下方法只负责原型的展示与动效，不含业务 ========
    // 五个考试入口：与桌面端一致，直接进等级选择页；tab 通过 query 带过去
    goChooseExam(tab) {
      analytics.logEvent('hskweb_getstarted_click', { position: 'exam_options', exam: tab })
      this.$router.push({ path: '/chooselevel/index', query: { tab } })
    },
    // Need help → Contact：与 Header 里的「Contact」同一去向
    goContact() {
      this.$router.push({ path: '/feedback' })
    },
    initHomeV2Motion() {
      this.initRevealAnimations()
      this.initProofSequence()
      this.initAiCarousel()
      this.initExperienceShowcase()
      document.addEventListener('visibilitychange', this.onHomeVisibilityChange)
      window.addEventListener('resize', this.onHomeResize, { passive: true })
    },
    teardownHomeV2() {
      this.homeObservers.forEach((o) => o.disconnect())
      this.homeObservers = []
      window.cancelAnimationFrame(this.proofFrame)
      this.stopAiAuto()
      this.clearAiCounter()
      this.stopExperienceAuto()
      if (this.testimonialAnimation) this.testimonialAnimation.cancel()
      this.testimonialAnimation = null
      clearInterval(this.centreCardTimer)
      clearTimeout(this.testimonialResizeTimer)
      document.removeEventListener('visibilitychange', this.onHomeVisibilityChange)
      window.removeEventListener('resize', this.onHomeResize)
    },
    onHomeVisibilityChange() {
      if (document.hidden) {
        this.stopAiAuto()
        this.stopExperienceAuto()
        // 标签页切走时也停掉居中卡片的轮询，回来再起
        clearInterval(this.centreCardTimer)
        this.centreCardTimer = null
      } else {
        this.startAiAuto()
        this.startExperienceAuto()
        if (this.$refs.testimonialRail && !this.centreCardTimer) {
          this.updateCentreCard()
          this.centreCardTimer = setInterval(this.updateCentreCard, 240)
        }
      }
    },
    onHomeResize() {
      clearTimeout(this.testimonialResizeTimer)
      this.testimonialResizeTimer = setTimeout(this.buildTestimonialAnimation, 160)
    },
    observe(target, callback, options) {
      if (!target) return
      if (!('IntersectionObserver' in window)) {
        callback(true)
        return
      }
      const observer = new IntersectionObserver((entries) => callback(entries.some((e) => e.isIntersecting), entries), options)
      observer.observe(target)
      this.homeObservers.push(observer)
      return observer
    },
    // ---- 滚动显现（原型 revealGroups 的等价实现）----
    initRevealAnimations() {
      const root = this.$refs.homeRoot
      if (!root) return
      const groups = [
        { selector: '#examOptions .exam-choice-card', type: '', stagger: 65 },
        { selector: '#learnerResults .testimonials-rail', type: 'hsk-reveal-media', stagger: 0 },
        { selector: '#institutions .b2b-v41-heading, #institutions .b2b-v41-copy p, #institutions .b2b-v41-points span, #institutions .b2b-v42-cta', type: '', stagger: 55 },
        { selector: '#institutions .b2b-v41-visual', type: 'hsk-reveal-right hsk-reveal-media', stagger: 0 },
        { selector: '#mobileApp .app-brand-copy', type: 'hsk-reveal-left', stagger: 0 },
        { selector: '#mobileApp .app-visual-collage', type: 'hsk-reveal-right hsk-reveal-media', stagger: 0 },
        { selector: '#help .help-card', type: '', stagger: 75 },
        { selector: '#help .simple-help-mascot', type: 'hsk-reveal-media', stagger: 0 },
      ]
      const items = []
      groups.forEach((group) => {
        root.querySelectorAll(group.selector).forEach((el, index) => {
          el.classList.add('hsk-reveal')
          if (group.type) group.type.split(' ').forEach((c) => el.classList.add(c))
          el.style.setProperty('--hsk-reveal-delay', `${index * group.stagger}ms`)
          items.push(el)
        })
      })
      if (this.reduceMotion || !('IntersectionObserver' in window)) {
        items.forEach((el) => el.classList.add('is-visible'))
        return
      }
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible')
              observer.unobserve(entry.target)
            }
          })
        },
        { threshold: 0.12, rootMargin: '0px 0px -7% 0px' }
      )
      items.forEach((el) => observer.observe(el))
      this.homeObservers.push(observer)
    },
    // ---- 三个可信度要点的入场（原型 definitive-proof-sequence 的等价实现）----
    // 原型 CSS 把 .proof-rail-section .proof-rail-item 写死成 opacity:0 !important，只有行内 !important 能压过，
    // 所以这里和原型一样用行内样式做入场：先画一帧，再按 220 / 460 / 700ms 依次露出 CTI、600K+、真题内容
    initProofSequence() {
      const root = this.$refs.homeRoot
      if (!root) return
      const items = Array.from(root.querySelectorAll('.proof-rail-section .proof-rail-item'))
      if (!items.length) return
      if (this.reduceMotion) {
        items.forEach((item) => {
          item.style.setProperty('opacity', '1', 'important')
          item.style.setProperty('transform', 'none', 'important')
          item.style.setProperty('transition', 'none', 'important')
        })
        return
      }
      items.forEach((item) => {
        item.style.setProperty('opacity', '0', 'important')
        item.style.setProperty('transform', 'translate3d(0,20px,0)', 'important')
        item.style.setProperty('transition', 'none', 'important')
      })
      this.proofFrame = window.requestAnimationFrame(() => {
        this.proofFrame = window.requestAnimationFrame(() => {
          items.forEach((item, index) => {
            const delay = 220 + index * 240 + 'ms'
            const ease = ' .72s cubic-bezier(.22,.72,.22,1) '
            item.style.setProperty('transition', 'opacity' + ease + delay + ', transform' + ease + delay, 'important')
            item.style.setProperty('opacity', '1', 'important')
            item.style.setProperty('transform', 'translate3d(0,0,0)', 'important')
          })
        })
      })
    },
    // ---- AI Score Report 轮播：进入视口后才开始计数与自动轮播 ----
    initAiCarousel() {
      this.observe(
        this.$refs.aiSection,
        (visible) => {
          if (visible && !this.aiActivated) {
            this.aiActivated = true
            this.showAiSlide(0, false)
            this.startAiAuto()
          }
        },
        { threshold: 0.28, rootMargin: '0px 0px -8% 0px' }
      )
    },
    showAiSlide(next, restart = true) {
      const count = 4
      this.aiIndex = (next + count) % count
      if (this.aiIndex === 0) this.runAiScoreCount()
      else this.clearAiCounter()
      if (restart) this.startAiAuto()
    },
    onAiDotClick(i) {
      this.aiActivated = true
      this.showAiSlide(i, true)
    },
    startAiAuto() {
      if (this.reduceMotion || !this.aiActivated) return
      clearInterval(this.aiTimer)
      this.aiTimer = setInterval(() => this.showAiSlide(this.aiIndex + 1, false), 5200)
    },
    stopAiAuto() {
      clearInterval(this.aiTimer)
      this.aiTimer = null
    },
    onAiFocusOut(e) {
      const carousel = this.$refs.aiCarousel
      if (!carousel || !carousel.contains(e.relatedTarget)) this.startAiAuto()
    },
    clearAiCounter() {
      if (this.aiRaf) cancelAnimationFrame(this.aiRaf)
      clearTimeout(this.aiCountDelay)
      this.aiRaf = null
      this.aiCountDelay = null
    },
    // 分数从 0 数到 248，数完亮出 PASSED（原型示意数据）
    runAiScoreCount() {
      this.clearAiCounter()
      this.aiScoreDisplay = '0'
      this.aiPassedVisible = false
      if (this.reduceMotion) {
        this.aiScoreDisplay = '248'
        this.aiPassedVisible = true
        return
      }
      this.aiCountDelay = setTimeout(() => {
        const target = 248
        const duration = 1700
        const start = performance.now()
        const tick = (now) => {
          const progress = Math.min(1, (now - start) / duration)
          const eased = 1 - Math.pow(1 - progress, 3)
          this.aiScoreDisplay = String(Math.round(target * eased))
          if (progress < 1) {
            this.aiRaf = requestAnimationFrame(tick)
          } else {
            this.aiScoreDisplay = '248'
            this.aiPassedVisible = true
            this.aiRaf = null
          }
        }
        this.aiRaf = requestAnimationFrame(tick)
      }, 340)
    },
    // ---- Practise the HSK Online：在视口内自动轮播，支持左右滑动 ----
    initExperienceShowcase() {
      this.observe(
        this.$refs.experienceSection,
        (visible, entries) => {
          this.experienceVisible = entries ? entries.some((e) => e.isIntersecting && e.intersectionRatio > 0.18) : true
          if (this.experienceVisible) this.startExperienceAuto()
          else this.stopExperienceAuto()
        },
        { threshold: [0, 0.18, 0.45] }
      )
      this.showExperience(0)
      this.startExperienceAuto()
    },
    showExperience(next) {
      const count = this.experienceItems.length
      this.experienceIndex = (next + count) % count
      const track = this.$refs.experienceTrack
      if (track && track.style) {
        track.style.setProperty(
          'transform',
          `translate3d(-${this.experienceIndex * 100}%,0,0)`,
          'important'
        )
      }
    },
    startExperienceAuto() {
      this.stopExperienceAuto()
      if (this.reduceMotion || !this.experienceVisible) return
      this.experienceTimer = setInterval(() => this.showExperience(this.experienceIndex + 1), 4800)
    },
    stopExperienceAuto() {
      clearInterval(this.experienceTimer)
      this.experienceTimer = null
    },
    onExperiencePointerDown(e) {
      this.experienceSwipeStartX = e.clientX
    },
    onExperiencePointerUp(e) {
      if (this.experienceSwipeStartX === null) return
      const dx = e.clientX - this.experienceSwipeStartX
      this.experienceSwipeStartX = null
      if (Math.abs(dx) > 45) {
        this.showExperience(this.experienceIndex + (dx < 0 ? 1 : -1))
        this.startExperienceAuto()
      }
    },
    // ---- 评价区无缝跑马灯（Web Animations API），悬停/聚焦暂停 ----
    initTestimonialMarquee() {
      clearInterval(this.centreCardTimer)
      this.centreCardTimer = null
      if (!this.$refs.testimonialRail) return
      this.buildTestimonialAnimation()
      this.updateCentreCard()
      this.centreCardTimer = setInterval(this.updateCentreCard, 240)
    },
    buildTestimonialAnimation() {
      const track = this.$refs.testimonialTrack
      if (!track) return
      const group = track.querySelector('.testimonials-group')
      if (this.testimonialAnimation) this.testimonialAnimation.cancel()
      this.testimonialAnimation = null
      track.style.transform = 'translateX(0)'
      if (this.reduceMotion || !group || typeof track.animate !== 'function') return
      // 两组之间的间距按 CSS 实际的 gap 取（原型 CSS 是 20px），否则循环接缝处会跳一下
      const groupGap = parseFloat(getComputedStyle(track).columnGap) || 16
      const pixelsPerSecond = 18
      const distance = group.getBoundingClientRect().width + groupGap
      if (distance <= 0) return
      this.testimonialAnimation = track.animate([{ transform: 'translateX(0)' }, { transform: `translateX(-${distance}px)` }], {
        duration: (distance / pixelsPerSecond) * 1000,
        iterations: Infinity,
        easing: 'linear',
      })
      if (this.testimonialPaused) this.testimonialAnimation.pause()
    },
    pauseTestimonials() {
      this.testimonialPaused = true
      this.testimonialAnimation && this.testimonialAnimation.pause()
    },
    resumeTestimonials() {
      this.testimonialPaused = false
      this.testimonialAnimation && this.testimonialAnimation.play()
    },
    onTestimonialFocusOut(e) {
      const rail = this.$refs.testimonialRail
      if (!rail || !rail.contains(e.relatedTarget)) this.resumeTestimonials()
    },
    // 离视口中线最近的那张卡片加高亮：两组（原组 + 复制组）里一起找，跟原型一样；
    // centreCardKey 给模板定位具体那张，centreCardIndex（组内下标）给埋点 getReviewIds 用
    updateCentreCard() {
      const rail = this.$refs.testimonialRail
      if (!rail) return
      const box = rail.getBoundingClientRect()
      const centre = box.left + box.width / 2
      let bestKey = ''
      let bestIndex = -1
      let bestDistance = Infinity
      rail.querySelectorAll('.testimonials-group').forEach((group, groupIndex) => {
        Array.from(group.children).forEach((card, index) => {
          const r = card.getBoundingClientRect()
          const distance = Math.abs(r.left + r.width / 2 - centre)
          if (distance < bestDistance) {
            bestDistance = distance
            bestKey = groupIndex + ':' + index
            bestIndex = index
          }
        })
      })
      if (bestKey !== this.centreCardKey) {
        this.centreCardKey = bestKey
        this.centreCardIndex = bestIndex
      }
    },
  },
}
</script>

<style lang="scss">
@import './home/home-v2.scss';
</style>
